import React, { useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Anchor,
  Button,
  Container,
  Divider,
  Group,
  LoadingOverlay,
  Paper,
  Radio,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { IconExclamationCircle } from '@tabler/icons-react';
import useAuthorizableProfiles from 'hooks/aiConnect/useAuthorizableProfiles';
import useAuthorizeMCPProfile from 'hooks/aiConnect/useAuthorizeMCPProfile';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import { useSearchParams } from 'react-router-dom';
import { AuthorizableProfile } from 'types';
import { KILO_PRIVACY_POLICY_URL } from 'utils/external-resources';
import { buildMCPDenyUrl, isTrustedMCPUrl, readOAuthState } from 'utils/mcp-api-client';
import notificationHandler from 'utils/notifications';

/**
 * Profile-selection step of the Claude MCP OAuth flow.
 *
 * kilo_mcp redirects the owner here with `?handoff=<token>&next=<authorize url>`. We record
 * the pick against that handoff via the kilo API — which fills the shared
 * `MCPPendingSelection` row — then send the browser back to `next` so kilo_mcp can read the
 * row and finish the handshake. No cookie is shared between the two domains.
 *
 * `next` arrives in the URL, so it is only followed after `isTrustedMCPUrl` confirms it
 * points at the configured kilo_mcp origin.
 */
const ClaudeSelectProfile = (): JSX.Element => {
  const [searchParams] = useSearchParams();
  const nextUrl = searchParams.get('next');
  const handoff = searchParams.get('handoff');

  const { data, isLoading, isError, error } = useAuthorizableProfiles();
  const authorizeMutation = useAuthorizeMCPProfile();
  const { errorNotification } = notificationHandler();
  const { trackAIConnectAuthorizeViewed, trackAIConnectAuthorized, trackAIConnectDenied } =
    useStaffTracking();

  const [selected, setSelected] = useState<string | null>(null);
  const profiles = useMemo(() => data?.data ?? [], [data]);

  // A request is only actionable with both params present and a `next` we can trust.
  const isMissingRequest = !nextUrl || !handoff || !isTrustedMCPUrl(nextUrl);
  const isForbidden = isError && error?.response?.status === 403;

  // Owner profiles grouped by gym, so a multi-gym owner sees which gym each pick authorizes.
  const gymGroups = useMemo(() => {
    return profiles.reduce<{ gymId: string; gymTitle: string; profiles: AuthorizableProfile[] }[]>(
      (groups, profile) => {
        const group = groups.find(candidate => candidate.gymId === profile.gymId);
        if (group) {
          group.profiles.push(profile);
        } else {
          groups.push({ gymId: profile.gymId, gymTitle: profile.gymTitle, profiles: [profile] });
        }
        return groups;
      },
      [],
    );
  }, [profiles]);

  useEffect(() => {
    if (!isMissingRequest) trackAIConnectAuthorizeViewed();
  }, [isMissingRequest, trackAIConnectAuthorizeViewed]);

  // Default to the first eligible profile so a single-gym owner can just click Authorize.
  useEffect(() => {
    if (!selected && profiles.length > 0) {
      setSelected(profiles[0].profileId);
    }
  }, [profiles, selected]);

  const handleAuthorize = (): void => {
    if (!selected || !nextUrl || !handoff || isMissingRequest) return;
    authorizeMutation.mutate(
      { handoff, profileId: selected },
      {
        onError: err => {
          errorNotification('Could not authorize Claude', err.response?.data.message);
        },
        onSuccess: () => {
          const gym = profiles.find(profile => profile.profileId === selected);
          trackAIConnectAuthorized({ gym_id: gym?.gymId ?? '', gym_count: gymGroups.length });
          // Full navigation, not client-side: the flow resumes on kilo_mcp, which reads the
          // now-filled handoff row by token.
          window.location.href = nextUrl;
        },
      },
    );
  };

  // Cancel is a real OAuth denial, not a dead end — kilo_mcp hands `access_denied` back to
  // the client so Claude reports a declined connection instead of hanging.
  const handleCancel = (): void => {
    trackAIConnectDenied();
    if (!handoff || isMissingRequest || !nextUrl) {
      window.location.href = '/';
      return;
    }
    window.location.href = buildMCPDenyUrl(handoff, readOAuthState(nextUrl));
  };

  return (
    <Container size='sm' py='xl' data-cy='claudeSelectProfile'>
      <Title align='center' order={2} mt='md'>
        Connect Claude to Kilo
      </Title>
      <Text align='center' color='dimmed' size='sm' mt='xs'>
        Claude is asking to read your gym&apos;s analytics. Choose which gym to authorize.
      </Text>

      <Stack mt='xl' pos='relative'>
        <LoadingOverlay visible={isLoading} />

        {isMissingRequest && (
          <Alert
            icon={<IconExclamationCircle size={18} />}
            color='red'
            title='Missing authorization request'
            data-cy='claudeSelectProfileMissingRequest'
          >
            This page was opened without an active Claude connection request. Start the connection from
            Claude&apos;s custom-connector setup.
          </Alert>
        )}

        {isForbidden && (
          <Alert
            icon={<IconExclamationCircle size={18} />}
            color='red'
            title='Owners only'
            data-cy='claudeSelectProfileForbidden'
          >
            Only gym owners can connect Claude. Ask an owner of your gym to set up the connection.
          </Alert>
        )}

        {!isMissingRequest && !isForbidden && gymGroups.length > 0 && (
          <Radio.Group value={selected ?? undefined} onChange={setSelected} name='mcp-gym'>
            <Stack w='100%'>
              {gymGroups.map(group => (
                <Paper key={group.gymId} withBorder radius='md' p='md' data-cy='claudeSelectProfileGymGroup'>
                  <Text fw={500}>{group.gymTitle}</Text>
                  <Stack spacing='xs' mt='xs'>
                    {group.profiles.map(profile => (
                      <Radio
                        key={profile.profileId}
                        value={profile.profileId}
                        label={`Authorizing as ${profile.staffName}`}
                      />
                    ))}
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Radio.Group>
        )}

        {!isMissingRequest && !isForbidden && !isLoading && gymGroups.length === 0 && (
          <Text align='center' color='dimmed' size='sm'>
            No gyms are available to connect.
          </Text>
        )}

        {!isMissingRequest && !isForbidden && (
          <>
            <Divider />
            <Text size='xs' color='dimmed'>
              Connecting Kilo to an AI provider constitutes acceptance of Kilo&apos;s{' '}
              <Anchor
                href={KILO_PRIVACY_POLICY_URL}
                target='_blank'
                rel='noreferrer'
                size='xs'
                data-cy='claudeSelectProfilePolicyLink'
              >
                privacy policy
              </Anchor>
              . You can revoke access any time from Gym Settings → AI Connect.
            </Text>
          </>
        )}

        <Group position='apart' mt='md'>
          <Button
            variant='subtle'
            color='gray'
            data-cy='claudeSelectProfileCancelButton'
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            data-cy='claudeSelectProfileAuthorizeButton'
            disabled={!selected || isMissingRequest || isForbidden}
            loading={authorizeMutation.isLoading}
            onClick={handleAuthorize}
          >
            Authorize
          </Button>
        </Group>
      </Stack>
    </Container>
  );
};

export default ClaudeSelectProfile;
